// Capabilities — §9's sixth section. Founder/admin.
//
// "installed Cipher capabilities + OSS libraries/services; config,
// versions/status, and where each capability is used."
//
// READS THE REGISTRY THAT ALREADY EXISTS
// ======================================
// `/api/brain/capabilities` is real, founder-gated and populated — 245 records
// on the production database. §9's three clauses map onto it almost exactly:
//
//   installed capabilities   GET /api/brain/capabilities
//   versions / status        the `version_fingerprint` and `instrumented`
//                            fields on each record
//   where each is used       GET /api/brain/capabilities/coverage  and
//                            GET /api/brain/capabilities/unused
//
// No endpoint is added and none is needed (R4).
//
// INSTRUMENTATION IS THE NUMBER THAT MATTERS AND IT IS REPORTED, NOT SMOOTHED
// ===========================================================================
// The build instruction's §4 warns twice about this surface's data, and its own
// correction warns in both directions: do not fabricate utilisation, and do not
// hardcode "empty" as a fact either — read the field and render what is there.
// So coverage is read live and whatever it answers is displayed. If most of the
// registry is uninstrumented, this page says so in the registry's own numbers
// rather than in a caveat written tonight that may be wrong by morning.
import React from "react";

import { ENDPOINTS } from "../api";
import { Block, Gate, Row, Table, Value } from "../ui";
import useResource from "../useResource";

const listOf = (d) => (Array.isArray(d) ? d : d?.capabilities || d?.items || []);

function Coverage() {
  const coverage = useResource(ENDPOINTS.capabilityCoverage);

  return (
    <Block
      title="Coverage"
      note="How much of the registry is instrumented, from the registry itself."
    >
      <Gate resource={coverage} empty="Coverage has not been computed.">
        {(d) => (
          <>
            {Object.entries(d || {})
              .filter(([, v]) => v === null || ["string", "number", "boolean"].includes(typeof v))
              .map(([k, v]) => (
                <Row key={k} label={k.replace(/_/g, " ")}>
                  <Value mono>{v === null ? null : String(v)}</Value>
                </Row>
              ))}
          </>
        )}
      </Gate>
    </Block>
  );
}

function Installed() {
  const caps = useResource(ENDPOINTS.capabilities, {
    isEmpty: (d) => !listOf(d).length,
  });

  return (
    <Block
      title="Installed capabilities"
      note="Skills, MCP servers, MCP tools and internal tools Cipher has registered."
    >
      <Gate resource={caps} empty="The capability registry is empty in this environment.">
        {(d) => {
          const items = listOf(d);
          return (
            <>
              <p className="set-note dim">
                {items.length} record{items.length === 1 ? "" : "s"} shown.
              </p>
              <Table head={["Capability", "Kind", "Instrumented", "Version"]}>
                {items.slice(0, 50).map((c) => (
                  <tr key={c.capability_id || c.natural_key || c.name}>
                    <td>{c.name || c.natural_key}</td>
                    <td className="mono dim">{c.kind || "—"}</td>
                    <td className="mono">{c.instrumented ? "yes" : "no"}</td>
                    <td className="mono dim">{c.version_fingerprint || "—"}</td>
                  </tr>
                ))}
              </Table>
            </>
          );
        }}
      </Gate>
    </Block>
  );
}

function Unused() {
  const unused = useResource(ENDPOINTS.capabilityUnused, {
    isEmpty: (d) => !listOf(d).length,
  });

  return (
    <Block
      title="Never used"
      note="Registered capabilities with no recorded usage. §9's 'where each capability is used', from the other end."
    >
      <Gate resource={unused} empty="Every registered capability has been used at least once.">
        {(d) => (
          <Table head={["Capability", "Kind"]}>
            {listOf(d)
              .slice(0, 50)
              .map((c) => (
                <tr key={c.capability_id || c.natural_key || c.name}>
                  <td>{c.name || c.natural_key}</td>
                  <td className="mono dim">{c.kind || "—"}</td>
                </tr>
              ))}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

export default function Capabilities() {
  return (
    <>
      <Coverage />
      <Installed />
      <Unused />
    </>
  );
}
